import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import { createNumberFilter } from '../../filters/types/numberFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/index';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type TableColumnFormatNumber<TRow> = {
  type: 'number';
  field: KeyOfType<TRow, number | number[] | null>;
  decimals?: number;
} & TableColumnFormatLink<TRow>;

type NumberColumnConfig<TRow, TDataType> = {
  decimals?: number;
} & TableDataColumnConfig<TRow, TDataType> &
  TableLinkColumnConfig<TRow>;

export const createNumberColumn: {
  <TRow>(
    config: NumberColumnConfig<TRow, number | null> & { array?: false },
  ): TableColumnBase<TRow, TableColumnFormatNumber<TRow>>;
  <TRow>(
    config: NumberColumnConfig<TRow, number[] | null> & { array: true },
  ): TableColumnBase<TRow, TableColumnFormatNumber<TRow>>;
} = <TRow>(
  config: NumberColumnConfig<TRow, number | number[] | null>,
): TableColumnBase<TRow, TableColumnFormatNumber<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'number',
    field: config.key,
    decimals: config.decimals,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
  ),
  toCsvString: (row) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, number | number[] | null>(row, config.key),
      (v: number | null): string => v?.toString(10) ?? '',
    ),
  filter: createDefaultFilter(config.filter, createNumberFilter()),
});
